/* istanbul ignore file  -- needs refactor */
import { Autocomplete, Button, styled, TextField } from '@ori-ui/mui';
import { axios } from '@ori/axios';
import { useLogger } from '@ori/logger';
import useTestId from '@ori/testid-generator';
import type { FC, ChangeEvent, SyntheticEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useTranslations } from '../../hooks/useTranslations';
import {
  type AccessConsultantResponse,
  type ConsultantNumberHint,
  type GetConsultantNumbersResponse,
  type VipAccessLabels,
  type VipAccessValidateResponse,
} from '../../models';
import { vipOrderUrls } from '../../utils';
import { ORDER_FORM_ROOT, ORDER_ON_BEHALF_DIALOG_CHANGE_CONSULTANT_INPUT } from './constants';

const VipOrderFormRoot = styled('form')(({ theme }) => ({
  alignItems: 'center',
  columnGap: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  overflowX: 'hidden',
}));

export interface VipOrderFormProps {
  vipAccessLabels: VipAccessLabels;
}

export const ChangeConsultantForm: FC<VipOrderFormProps> = ({
  vipAccessLabels: { ConsultantChangeConsultantNumber },
}) => {
  const logger = useLogger('ChangeConsultantForm');
  const abortControllerRef = useRef(new AbortController());
  const formRef = useRef<HTMLFormElement>(null);
  const [areHintsLoading, setAreHintsLoading] = useState(false);
  const [consultantNumber, setConsultantNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hints, setHints] = useState<ConsultantNumberHint[]>([]);
  const [isSubmitLocked, setIsSubmitLocked] = useState(true);
  const { translations } = useTranslations();
  const { register, handleSubmit } = useForm();
  const { getTestId } = useTestId();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setConsultantNumber(value);
      setErrorMessage(null);
      setAreHintsLoading(true);
      setIsSubmitLocked(true);

      if (value) {
        axios
          .get<GetConsultantNumbersResponse>(vipOrderUrls.consultantNumbers(value), {
            signal: abortControllerRef.current.signal,
          })
          .then(({ data }) => setHints(data.map((hint, index) => ({ index, hint }))))
          .catch((error: unknown) => {
            if (error instanceof Error && error.name !== 'AbortError') {
              logger.error(new Error('Cannot get consultant numbers', { cause: error }));
            }
          })
          .finally(() => {
            setAreHintsLoading(false);
          });
      }
    },
    [logger],
  );

  const handleSelect = useCallback(
    (event: SyntheticEvent) => {
      const { textContent: id } = event.target as HTMLElement;
      setConsultantNumber(id ?? '');
      setHints([]);

      if (id) {
        axios
          .post<VipAccessValidateResponse>(vipOrderUrls.accessSwitchValidate(id))
          .then(({ data: { message, result } }) => {
            setErrorMessage(message);
            setIsSubmitLocked(!result);
          })
          .catch((error: unknown) => logger.error(new Error('Cannot validate access switch', { cause: error })));
      }
    },
    [logger],
  );

  const onSubmit = useCallback(async () => {
    try {
      const { data } = await axios.get<AccessConsultantResponse>(vipOrderUrls.accessConsultant(consultantNumber));
      const { ConsultantNumber: consultant, ErrorMessage, IsAccessGranted } = data;

      if (IsAccessGranted) {
        try {
          await axios.post(vipOrderUrls.accessSwitch(consultant));
          formRef.current?.submit();
        } catch (error: unknown) {
          setErrorMessage(translations.failedToSubmit);
          logger.error(new Error('Cannot post VipAccessSwitch', { cause: error }));
        }
      } else {
        setErrorMessage(ErrorMessage);
      }
    } catch (error: unknown) {
      logger.error(new Error('Cannot get consultant info', { cause: error }));
    }
  }, [consultantNumber, translations.failedToSubmit, logger]);

  return (
    <VipOrderFormRoot
      ref={formRef}
      data-testid={getTestId(ORDER_FORM_ROOT)}
      action={vipOrderUrls.basket}
      autoComplete="off"
      method="POST"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Autocomplete
        fullWidth
        getOptionLabel={(hint) => String(hint.hint)}
        id="ConsultantNumber"
        loading={areHintsLoading}
        loadingText={translations.loading}
        noOptionsText={translations.noOptions}
        options={hints}
        renderInput={(props) => (
          <TextField
            data-testid={getTestId(ORDER_ON_BEHALF_DIALOG_CHANGE_CONSULTANT_INPUT)}
            variant="standard"
            error={Boolean(errorMessage)}
            helperText={errorMessage}
            label={ConsultantChangeConsultantNumber}
            {...register(props.id)}
            onChange={handleChange}
            {...props}
            fullWidth
          />
        )}
        onChange={handleSelect}
      />
      <Button
        fullWidth
        sx={{ marginTop: '1em' }}
        disabled={isSubmitLocked}
        color="primary"
        type="submit"
        variant="contained"
      >
        {translations.change}
      </Button>
    </VipOrderFormRoot>
  );
};
